import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';
import { TestimonialsService } from '../../../core/services/testimonials.service';
import { Testimonial } from "../../../core/models/testimonials.types";
import { Observable } from 'rxjs';

interface Feature {
  title: string;
  description: string;
  icon: string;
  color: string;
  gradientFrom: string;
  gradientTo: string;
}

interface Announcement {
  title: string;
  ctaText: string;
  ctaUrl: string;
}

interface Stat {
  value: string;
  label: string;
  icon: string;
  color: string;
  gradientFrom: string;
  gradientTo: string;
}

interface Product {
  title: string;
  description: string;
  image: string;
  imageAlt: string;
  link: string;
  category?: string;
  features?: string[];
  isFeatured?: boolean;
  isComplimentary?: boolean;
}

interface ProcessStep {
  number: number;
  title: string;
  description: string;
}

interface Industry {
  name: string;
  description: string;
  icon: string;
  iconColor: string;
  gradientFrom: string;
  gradientTo: string;
  benefits: string[];
}

interface FAQ {
  question: string;
  answer: string;
}

interface Solution {
  title: string;
  description: string;
  features: string[];
  ctaText: string;
  ctaLink: string;
}

interface Logo {
  name: string;
  imageURL: string;
  imgName: string;
}

@Component({
  selector: 'app-landing',
  templateUrl: './landing.component.html',
  styleUrl: './landing.component.css',
  standalone: true,
  imports: [CommonModule, RouterModule]
})
export class LandingComponent implements OnInit {
  // Testimonials data
  landingPageTestimonials$: Observable<Testimonial[]>;

  // Mock announcement data - this will come from API eventually
  announcement: Announcement | null = null;

  companyHistory = {
    title: 'Our 25+ Year Legacy of Excellence',
    description: 'For over 25 years, Industry Intelligence Inc. has been a leader in delivering relevant market intelligence solutions across various industries. Our brand represents trust, innovation, and expertise.',
    milestones: [
      {
        year: '2000',
        title: 'Founded',
        description: 'Established to provide market intelligence for the forest products industry'
      },
      {
        year: '2005',
        title: 'Digital Transformation',
        description: 'Launched our first online platform for real-time market intelligence'
      },
      {
        year: '2012',
        title: 'Industry Expansion',
        description: 'Extended our solutions to packaging and chemical manufacturing sectors'
      },
      {
        year: '2023',
        title: 'Global Reach',
        description: 'Now serving clients in over 100 countries with comprehensive intelligence solutions'
      },
      {
        year: '2025',
        title: 'AI Integration',
        description: 'Planned integration of next-generation AI and machine learning capabilities'
      }
    ]
  };

  features: Feature[] = [
    {
      title: 'Real-time Market Analytics',
      description: 'Track market trends, competitor movements, and industry changes as they happen with our AI-powered analytics engine.',
      icon: 'chart-line',
      color: 'blue',
      gradientFrom: 'blue-500/10',
      gradientTo: 'purple-500/10'
    },
    {
      title: 'Legislative Monitoring',
      description: 'Stay compliant with automated legislative tracking and real-time alerts for regulatory changes affecting your industry.',
      icon: 'check-circle',
      color: 'green',
      gradientFrom: 'green-500/10',
      gradientTo: 'blue-500/10'
    },
    {
      title: 'Predictive Insights',
      description: 'Leverage AI and machine learning to predict market trends and get ahead of industry changes before they happen.',
      icon: 'lightning-bolt',
      color: 'purple',
      gradientFrom: 'purple-500/10',
      gradientTo: 'pink-500/10'
    }
  ];

  stats: Stat[] = [
    {
      value: '500K+',
      label: 'Legislations Tracked',
      icon: 'chart-bar',
      color: 'blue',
      gradientFrom: 'blue-500/10',
      gradientTo: 'purple-500/10'
    },
    {
      value: '100M+',
      label: 'News Articles Analyzed',
      icon: 'trending-up',
      color: 'green',
      gradientFrom: 'green-500/10',
      gradientTo: 'blue-500/10'
    },
    {
      value: '50K+',
      label: 'Daily Insights',
      icon: 'lightning-bolt',
      color: 'purple',
      gradientFrom: 'purple-500/10',
      gradientTo: 'pink-500/10'
    },
    {
      value: '98%',
      label: 'Client Satisfaction',
      icon: 'star',
      color: 'yellow',
      gradientFrom: 'yellow-500/10',
      gradientTo: 'orange-500/10'
    }
  ];

  products: Product[] = [
    {
      title: 'Legislation Monitor',
      description: 'This must-have weekly report provides business leaders with accurate, up-to-date information on legislative developments at the federal and state levels. Written with business in mind and free of technical jargon.',
      image: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?auto=format&fit=crop&q=80&w=1000',
      imageAlt: 'Legislation Monitor Dashboard',
      link: '/products/legislation-monitor',
      category: 'Featured Product',
      features: [
        'Comprehensive insight: From bills to statutes, with bill history and sponsor tracking',
        'Relevant summaries: AI-powered dashboard breaks down significance of each update',
        'Industry relevance: Quickly investigate updates using industry-specific categories',
        '360-degree connections: Links legislative updates with related news and analysis'
      ],
      isFeatured: true,
      isComplimentary: false
    },
    {
      title: 'Supplier IQ',
      description: 'Transform your supplier management with AI-powered insights, comprehensive risk assessment tools, and real-time monitoring of your entire supply chain ecosystem.',
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&q=80&w=1000',
      imageAlt: 'Supplier IQ Dashboard',
      link: '/products/supplier-iq',
      category: 'Supply Chain',
      features: [
        'Supplier risk assessment',
        'Performance monitoring',
        'Sustainability tracking',
        'Compliance verification'
      ],
      isFeatured: false,
      isComplimentary: false
    },
    {
      title: 'Competitive Edge',
      description: 'Gain strategic advantage with comprehensive competitor analysis, market positioning insights, and early detection of industry disruptions and opportunities.',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=1000',
      imageAlt: 'Competitive Edge Dashboard',
      link: '/products/competitive-edge',
      category: 'Market Analysis',
      features: [
        'Competitor benchmarking',
        'Strategic move alerts',
        'Market share analysis',
        'Innovation tracking'
      ],
      isFeatured: false,
      isComplimentary: false
    },
    {
      title: 'Customer Insight',
      description: 'Understand your customers better with deep behavioral analytics, sentiment analysis, and trend forecasting to drive product development and marketing strategies.',
      image: 'https://images.unsplash.com/photo-1551836022-d5d88e9218df?auto=format&fit=crop&q=80&w=1000',
      imageAlt: 'Customer Insight Dashboard',
      link: '/products/customer-insight',
      category: 'Customer Analytics',
      features: [
        'Behavioral analytics',
        'Sentiment tracking',
        'Trend forecasting',
        'Segmentation tools'
      ],
      isFeatured: false,
      isComplimentary: false
    },
    {
      title: 'Executive Summary',
      description: 'Get concise, actionable insights to help you make critical business decisions faster with our comprehensive industry intelligence reports.',
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&q=80&w=1000',
      imageAlt: 'Executive Summary Dashboard',
      link: '/products/executive-summary',
      category: 'Market Intelligence',
      features: [
        'Daily executive briefings',
        'Customizable dashboard',
        'Trend analysis and forecasting',
        'Export to PDF and Excel'
      ],
      isFeatured: false,
      isComplimentary: true
    }
  ];

  processSteps: ProcessStep[] = [
    {
      number: 1,
      title: 'Data Collection',
      description: 'We gather data from thousands of sources across industries in real-time.'
    },
    {
      number: 2,
      title: 'AI Analysis',
      description: 'Our AI algorithms process and analyze the data to identify patterns and insights.'
    },
    {
      number: 3,
      title: 'Insight Generation',
      description: 'We transform raw data into actionable intelligence tailored to your needs.'
    },
    {
      number: 4,
      title: 'Delivery & Action',
      description: 'Access insights through our platform, reports, and alerts to drive decisions.'
    }
  ];

  industries: Industry[] = [
    {
      name: 'Packaging Industry',
      description: 'Navigate sustainability regulations, supply chain challenges, and consumer preferences in the evolving packaging landscape.',
      icon: 'box',
      iconColor: 'blue-500',
      gradientFrom: 'blue-500/10',
      gradientTo: 'indigo-500/10',
      benefits: [
        'Sustainable packaging trends',
        'Regulatory compliance',
        'Material innovation insights'
      ]
    },
    {
      name: 'Forest Products',
      description: 'Stay informed on sustainable forestry practices, market demand shifts, and environmental regulations affecting forest products.',
      icon: 'tree',
      iconColor: 'green-500',
      gradientFrom: 'green-500/10',
      gradientTo: 'emerald-500/10',
      benefits: [
        'Sustainable forestry trends',
        'Supply chain optimization',
        'Carbon footprint reduction'
      ]
    },
    {
      name: 'Chemical Manufacturing',
      description: 'Transform your chemical operations with insights on sustainability, regulatory compliance, and innovation opportunities.',
      icon: 'flask',
      iconColor: 'purple-500',
      gradientFrom: 'purple-500/10',
      gradientTo: 'pink-500/10',
      benefits: [
        'Green chemistry adoption',
        'Emissions reduction strategies',
        'Circular economy implementation'
      ]
    }
  ];

  faqs: FAQ[] = [
    {
      question: 'How does Industry Intelligence gather its data?',
      answer: 'Our platform collects data from thousands of sources including news outlets, regulatory bodies, company reports, social media, and proprietary databases. We use advanced AI and machine learning algorithms to process and analyze this data in real-time, ensuring you receive the most current and relevant insights for your industry.'
    },
    {
      question: 'Can I customize the platform to focus on my specific industry needs?',
      answer: 'Yes, our platform is highly customizable. During onboarding, we work with you to configure dashboards, alerts, and reports that align with your specific industry, competitors, and strategic priorities. You can further refine these settings at any time through your user dashboard.'
    },
    {
      question: 'How often is the information updated?',
      answer: 'Our platform updates information in real-time. As soon as new data is available from our sources, it\'s processed, analyzed, and made available to you. Critical alerts are delivered instantly, while regular reports are generated daily, weekly, or monthly according to your preferences.'
    },
    {
      question: 'What kind of support do you provide?',
      answer: 'We provide comprehensive support including 24/7 technical assistance, regular training sessions, and a dedicated customer success manager for enterprise clients. Our knowledge base contains detailed guides and tutorials, and we offer regular webinars on new features and industry best practices.'
    },
    {
      question: 'How secure is my data on your platform?',
      answer: 'Security is our top priority. We employ enterprise-grade encryption, regular security audits, and comply with global data protection regulations including GDPR and CCPA. Our infrastructure is hosted on secure cloud providers with SOC 2 compliance, and we maintain strict access controls and authentication protocols.'
    }
  ];

  // Solutions data - simplified from Record to Array since it's no longer interactive
  solutions = {
    regulatory: {
      title: 'Regulatory Monitoring',
      description: 'Stay ahead of regulatory changes affecting your industry with proactive monitoring of federal and state legislation.',
      features: [
        'Proactive Intelligence: Stay ahead of changes and make informed decisions',
        'Relevant Summaries: Business-focused reports without legal jargon'
      ],
      ctaText: 'Protect Your Business',
      ctaLink: '/solutions/regulatory-monitoring',
      icon: 'building',
      color: 'blue'
    },
    customer: {
      title: 'Customer Insight',
      description: 'Gain deep understanding of customer behavior, preferences, and market trends to drive strategic decision-making.',
      features: [
        'Customer sentiment analysis',
        'Behavioral trend identification'
      ],
      ctaText: 'Explore Customer Intelligence',
      ctaLink: '/solutions/customer-insight',
      icon: 'users',
      color: 'purple'
    },
    competitive: {
      title: 'Competitive Analysis',
      description: 'Track competitor activities, product launches, and strategic moves with our comprehensive competitive intelligence platform.',
      features: [
        'Competitor benchmarking',
        'Strategic move alerts'
      ],
      ctaText: 'Explore Competitive Analysis',
      ctaLink: '/solutions/competitive-analysis',
      icon: 'chart',
      color: 'green'
    },
    supplyChain: {
      title: 'Supply Chain Insights',
      description: 'Optimize your supply chain with comprehensive supplier intelligence, risk assessment, and performance analytics.',
      features: [
        'Supplier risk assessment',
        'Supply chain mapping'
      ],
      ctaText: 'Explore Supply Chain Insights',
      ctaLink: '/solutions/supply-chain',
      icon: 'arrow-up',
      color: 'amber'
    },
    market: {
      title: 'Market Intelligence Center',
      description: 'Get real-time insights on market trends, pricing dynamics, and industry developments to stay ahead of the competition.',
      features: [
        'Industry trend analysis',
        'Pricing intelligence',
        'Market share tracking',
        'Growth opportunity identification'
      ],
      ctaText: 'Explore Market Intelligence',
      ctaLink: '/solutions/market-intelligence',
      icon: 'chart-bar',
      color: 'blue'
    }
  };

  logos: Logo[] = this.returnLogo();

  constructor(
    private meta: Meta,
    private title: Title,
    private testimonialsService: TestimonialsService
  ) {
       // Initialize the testimonials observable
       this.landingPageTestimonials$ = this.testimonialsService.getLandingPageTestimonials(3);
  }

  ngOnInit() {
    this.setupSEO();
  }

  private setupSEO() {
    this.title.setTitle('Industry Intelligence Inc. - Market Intelligence Platform');
    this.meta.updateTag({ 
      name: 'description', 
      content: 'Monitor legislation, track competitors, and stay ahead with real-time market insights all in one platform. Industry Intelligence provides comprehensive market analysis and business intelligence solutions.' 
    });
    this.meta.updateTag({ 
      property: 'og:title', 
      content: 'Industry Intelligence Inc. - Market Intelligence Platform' 
    });
    this.meta.updateTag({ 
      property: 'og:description', 
      content: 'Monitor legislation, track competitors, and stay ahead with real-time market insights all in one platform.' 
    });
    this.meta.updateTag({ 
      property: 'og:type', 
      content: 'website' 
    });
  }

  returnLogo(): Logo[] {
    return [
    {
      name: "Amcor plc",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/amcor.png",
      imgName: "amcor"
    },
    {
      name: "Berry Global, Inc.",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/berryglobal.png",
      imgName: "berryglobal"
    },
    {
      name: "Campbell Soup Company",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/campbells.png",
      imgName: "campbells"
    },
    {
      name: "Chick-Fil-A, Inc.",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/chickfila.png",
      imgName: "chickfila"
    },
    {
      name: "Church & Dwight, Inc.",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/churchdwight.png",
      imgName: "churchdwight"
    },
    {
      name: "Dow Inc.",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/dowchemical.png",
      imgName: "dowchemical"
    },
    {
      name: "General Mills Inc.",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/generalmills.png",
      imgName: "generalmills"
    },
    {
      name: "H.B. Fuller",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/hbfuller.png",
      imgName: "hbfuller"
    },
    {
      name: "Hexion Inc.",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/hexion.png",
      imgName: "hexion"
    },
    {
      name: "J.D. Irving, Limited",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/jdirving.png",
      imgName: "jdirving"
    },
    {
      name: "Novolex",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/novolex.png",
      imgName: "novolex"
    },
    {
      name: "Packaging Corporation of America",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/pca.png",
      imgName: "pca"
    },
    {
      name: "Pixelle Specialty Solutions LLC",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/pixelle.png",
      imgName: "pixelle"
    },
    {
      name: "Sealed Air Corporation",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/sealedair.png",
      imgName: "sealedair"
    },
    {
      name: "Weyerhaeuser Company",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/weyerhaeuser.png",
      imgName: "weyerhaeuser"
    },
    {
      name: "Graphic Packaging International",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/graphicpkg.png",
      imgName: "graphicpkg"
    },
    {
      name: "BERICAP Holding GmbH",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/bericap.png",
      imgName: "bericap"
    },
    {
      name: "Flexible Packaging Association",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/fpa.png",
      imgName: "fpa"
    },
    {
      name: "Green Bay Packaging Inc.",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/greenbaypackaging.png",
      imgName: "greenbaypackaging"
    },
    {
      name: "Liquibox",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/liquibox.png",
      imgName: "liquibox"
    },
    {
      name: "Menasha Packaging Company, LLC",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/menasha.png",
      imgName: "menasha"
    },
    {
      name: "Oji Fibre Solutions (NZ) Ltd.",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/oji.png",
      imgName: "oji"
    },
    {
      name: "ORBIS Corporation",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/orbis.png",
      imgName: "orbis"
    },
    {
      name: "Printpack",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/printpack.png",
      imgName: "printpack"
    },
    {
      name: "ProAmpac LLC",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/proampac.png",
      imgName: "proampac"
    },
    {
      name: "Sonoco Products Company",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/sonoco.png",
      imgName: "sonoco"
    },
    {
      name: "WestRock Company",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/westrock.png",
      imgName: "westrock"
    },
    {
      name: "Arauco",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/arauco.png",
      imgName: "arauco"
    },
    {
      name: "Boise Cascade",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/boisecascade.png",
      imgName: "boisecascade"
    },
    {
      name: "Brookfield Asset Management Inc.",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/brookfield.png",
      imgName: "brookfield"
    },
    {
      name: "BTG Pactual Timberland Investment Group (TIG)",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/btgtig.png",
      imgName: "btgtig"
    },
    {
      name: "Campbell Global, LLC",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/campbellglobal.png",
      imgName: "campbellglobal"
    },
    {
      name: "The Forestland Group ",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/forestlandgroup.png",
      imgName: "forestlandgroup"
    },
    {
      name: "Manulife Investment Management",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/manulifeim.png",
      imgName: "manulifeim"
    },
    {
      name: "Interfor Corporation",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/interfor.png",
      imgName: "interfor"
    },
    {
      name: "Louisiana-Pacific Corporation",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/lp.png",
      imgName: "lp"
    },
    {
      name: "Molpus Woodlands Group, LLC",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/molpus.png",
      imgName: "molpus"
    },
    {
      name: "PotlatchDeltic Corporation",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/potlatchdeltic.png",
      imgName: "potlatchdeltic"
    },
    {
      name: "Rayonier Inc.",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/rayonier.png",
      imgName: "rayonier"
    },
    {
      name: "Roseburg Forest Products",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/roseburg.png",
      imgName: "roseburg"
    },
    {
      name: "Ahlstrom",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/ahlstrommunksjo.png",
      imgName: "ahlstrommunksjo"
    },
    {
      name: "Canfor Corp.",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/canfor.png",
      imgName: "canfor"
    },
    {
      name: "Clearwater Paper Corporation",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/clearwater.png",
      imgName: "clearwater"
    },
    {
      name: "Domtar Corporation",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/domtar.png",
      imgName: "domtar"
    },
    {
      name: "Glatfelter Corporation",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/glatfelter.png",
      imgName: "glatfelter"
    },
    {
      name: "Kruger Inc.",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/kruger.png",
      imgName: "kruger"
    },
    {
      name: "Lindenmeyr Central A Division of Central National Gottesman Inc.",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/lindenmeyrcentral.png",
      imgName: "lindenmeyrcentral"
    },
    {
      name: "Marubeni America Corporation",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/marubeni.png",
      imgName: "marubeni"
    },
    {
      name: "Mercer International Inc.",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/mercer.png",
      imgName: "mercer"
    },
    {
      name: "Millar Western Forest Products Ltd.",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/millarwestern.png",
      imgName: "millarwestern"
    },
    {
      name: "Neenah, Inc.",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/neenah.png",
      imgName: "neenah"
    },
    {
      name: "Sappi Ltd.",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/sappi.png",
      imgName: "sappi"
    },
    {
      name: "Andritz AG",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/andritz.png",
      imgName: "andritz"
    },
    {
      name: "Evonik Industries AG",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/evonik.png",
      imgName: "evonik"
    },
    {
      name: "Flint Group",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/flintgroup.png",
      imgName: "flintgroup"
    },
    {
      name: "INX International Ink Co.",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/inxinternationalinks.png",
      imgName: "inxinternationalinks"
    },
    {
      name: "MSU - School of Packaging ",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/michiganstate.png",
      imgName: "michiganstate"
    },
    {
      name: "Seaspan ULC",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/seaspan.png",
      imgName: "seaspan"
    },
    {
      name: "Sun Chemical Corporation",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/sunchemical.png",
      imgName: "sunchemical"
    },
    {
      name: "U.S. Endowment for Forestry & Communities, Inc.",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/usendowment.png",
      imgName: "usendowment"
    },
    {
      name: "Warnell School of Forestry and Natural Resources",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/universitygeorgia.png",
      imgName: "universitygeorgia"
    },
    {
      name: "United Steelworkers (USW)",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/usw.png",
      imgName: "usw"
    },
    {
      name: "Valmet",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/valmet.png",
      imgName: "valmet"
    },
    {
      name: "Western Plastics Association",
      imageURL: "https://industryintel.s3.us-west-2.amazonaws.com/assets/images/clientlogo/westernplastics.png",
      imgName: "westernplastics"
    }
    ];
  }
} 